import React from 'react';
import {
    Box, Grid, Card, CardActionArea, CardContent, Typography, Chip, Tooltip,
} from '@mui/material';
import {
    Straighten as StraightenIcon,
    GridOn as GridOnIcon,
    AccountTree as AccountTreeIcon,
    CheckCircleOutline as CheckCircleIcon,
    Psychology as PsychologyIcon,
    Analytics as AnalyticsIcon,
    Schedule as ScheduleIcon,
} from '@mui/icons-material';

const STUDY_TYPES = [
    {
        key: 'type2',
        title: 'Type 2 Crossed GRR',
        subtitle: 'ANOVA Method',
        description: 'Repeatability & reproducibility across operators and parts.',
        icon: <GridOnIcon sx={{ fontSize: 32 }} />,
        status: 'active',
        color: '#90caf9',
    },
    {
        key: 'type1',
        title: 'Type 1 GRR',
        subtitle: 'Bias & Repeatability',
        description: 'Single operator, reference part — evaluates bias and Cg/Cgk.',
        icon: <StraightenIcon sx={{ fontSize: 32 }} />,
        status: 'active',
        color: '#90caf9',
    },
    {
        key: 'type3',
        title: 'Type 3 / Expanded GRR',
        subtitle: 'Nested or Multi-Factor',
        description: 'Nested designs or additional factors (fixture, environment, lab).',
        icon: <AccountTreeIcon sx={{ fontSize: 32 }} />,
        status: 'active',
        color: '#90caf9',
    },
    {
        key: 'attribute',
        title: 'Attribute Agreement',
        subtitle: 'Kappa Statistics',
        description: 'Operator agreement on pass/fail or categorical data.',
        icon: <CheckCircleIcon sx={{ fontSize: 32 }} />,
        status: 'active',
        color: '#90caf9',
    },
    {
        key: 'attributeAnalytic',
        title: 'Attribute Analytic',
        subtitle: 'Signal Detection',
        description: 'Inspection effectiveness — POD, false alarm rate, ROC analysis.',
        icon: <PsychologyIcon sx={{ fontSize: 32 }} />,
        status: 'active',
        color: '#90caf9',
    },
    {
        key: 'capabilityImpact',
        title: 'Measurement Impact',
        subtitle: 'Capability Decomposition',
        description: 'How much of your observed Cpk degradation comes from gage error?',
        icon: <AnalyticsIcon sx={{ fontSize: 32 }} />,
        status: 'active',
        color: '#90caf9',
    },
];

function StudyTypeCard({ type, onSelect }) {
    const isActive = type.status === 'active';

    const cardContent = (
        <Card
            sx={{
                height: '100%',
                backgroundColor: isActive ? 'rgba(144,202,249,0.06)' : 'rgba(255,255,255,0.03)',
                border: isActive ? '1px solid rgba(144,202,249,0.3)' : '1px solid rgba(255,255,255,0.08)',
                transition: 'all 0.2s ease',
                opacity: isActive ? 1 : 0.6,
            }}
        >
            <CardActionArea
                onClick={isActive ? () => onSelect(type.key) : undefined}
                disabled={!isActive}
                sx={{ height: '100%', p: 0.5 }}
            >
                <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box sx={{ color: type.color }}>
                            {type.icon}
                        </Box>
                        {isActive ? (
                            <Chip
                                label="Available"
                                size="small"
                                sx={{
                                    backgroundColor: 'rgba(76,175,80,0.15)',
                                    color: '#81c784',
                                    fontSize: '0.65rem',
                                    height: 20,
                                    fontWeight: 600,
                                }}
                            />
                        ) : (
                            <Chip
                                icon={<ScheduleIcon sx={{ fontSize: '0.65rem !important' }} />}
                                label="Coming Soon"
                                size="small"
                                sx={{
                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                    color: 'rgba(255,255,255,0.4)',
                                    fontSize: '0.65rem',
                                    height: 20,
                                }}
                            />
                        )}
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isActive ? '#e3f2fd' : 'rgba(255,255,255,0.5)', lineHeight: 1.3, mb: 0.25 }}>
                        {type.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isActive ? '#90caf9' : 'rgba(255,255,255,0.3)', display: 'block', mb: 0.75, fontSize: '0.7rem' }}>
                        {type.subtitle}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, fontSize: '0.72rem' }}>
                        {type.description}
                    </Typography>
                </CardContent>
            </CardActionArea>
        </Card>
    );

    if (!isActive) {
        return (
            <Tooltip title="This study type is under development and will be available in a future release." arrow placement="top">
                <Box sx={{ height: '100%' }}>{cardContent}</Box>
            </Tooltip>
        );
    }

    return cardContent;
}

function MSAStudyTypeSelector({ onSelect }) {
    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                Select Study Type
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', mb: 3 }}>
                Choose the measurement system analysis study that fits your needs.
            </Typography>
            <Grid container spacing={2}>
                {STUDY_TYPES.map((type) => (
                    <Grid item xs={12} sm={6} md={4} key={type.key} sx={{ display: 'flex' }}>
                        <StudyTypeCard type={type} onSelect={onSelect} />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

export default MSAStudyTypeSelector;
